import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mic, Square, Loader2, Replace, CornerDownRight } from "lucide-react";

const MIME_CANDIDATES = ["audio/webm", "audio/mp4", "audio/ogg"];

const pickMime = () => MIME_CANDIDATES.find(
  (m) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) || "";

const extFor = (mime) => (mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm");

const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// Press to record, press again to stop. Whisper transcribes, then you choose replace or append.
export default function DictateButton({ value = "", onChange, field = "diagnosis", caseId = "",
                                        label = "Dictate", disabled = false, testid = "dictate" }) {
  const [state, setState] = useState("idle"); // idle | recording | working
  const [seconds, setSeconds] = useState(0);
  const [result, setResult] = useState(null);
  const recorder = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);
  const ticker = useRef(null);

  const cleanup = useCallback(() => {
    clearInterval(ticker.current);
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    recorder.current = null;
    chunks.current = [];
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const send = async (blob, mime) => {
    setState("working");
    try {
      const form = new FormData();
      form.append("file", blob, `dictation.${extFor(mime)}`);
      const { data } = await api.post("/dictation/transcribe", form, {
        params: { field, case_id: caseId, keep_audio: caseId ? "true" : "false" },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      if (!data.text) {
        toast.error("Nothing was picked up — try again a little closer to the microphone");
        return;
      }
      if (data.audio_saved === false) {
        toast.warning("Text captured, but the audio copy could not be saved");
      }
      if (!value.trim()) {
        onChange(data.text);
        toast.success("Dictation added");
      } else {
        setResult(data.text);
      }
    } catch (e) { toast.error(apiError(e)); } finally { setState("idle"); setSeconds(0); }
  };

  const start = async () => {
    const mime = pickMime();
    if (!navigator.mediaDevices?.getUserMedia || !mime) {
      toast.error("This browser cannot record audio — try Chrome, Edge or Safari");
      return;
    }
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error("Microphone blocked — allow microphone access for this site and try again");
      return;
    }
    chunks.current = [];
    const rec = new MediaRecorder(stream.current, { mimeType: mime });
    rec.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunks.current, { type: mime });
      cleanup();
      if (blob.size > 1000) send(blob, mime);
      else { setState("idle"); setSeconds(0); toast.error("That recording was too short"); }
    };
    recorder.current = rec;
    rec.start();
    setState("recording");
    setSeconds(0);
    ticker.current = setInterval(() => setSeconds((s) => {
      if (s >= 599) { rec.state === "recording" && rec.stop(); return s; }
      return s + 1;
    }), 1000);
  };

  const stop = () => recorder.current?.state === "recording" && recorder.current.stop();

  const applyResult = (mode) => {
    if (mode === "replace") onChange(result);
    else onChange(`${value.replace(/\s+$/, "")}\n\n${result}`.trim());
    setResult(null);
    toast.success(mode === "replace" ? "Text replaced" : "Dictation appended");
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" disabled={disabled || state === "working"}
              onClick={state === "recording" ? stop : start} data-testid={`${testid}-button`}
              className={`h-9 gap-1.5 ${state === "recording"
                ? "animate-pulse border-red-600 bg-red-50 text-red-800"
                : "border-slate-300 text-[#134e4a] hover:border-[#134e4a]"}`}>
        {state === "working" ? <Loader2 size={14} className="animate-spin" />
          : state === "recording" ? <Square size={13} /> : <Mic size={14} />}
        {state === "working" ? "Transcribing…" : state === "recording" ? `Stop ${mmss(seconds)}` : label}
      </Button>

      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="max-w-lg" data-testid={`${testid}-choice-dialog`}>
          <DialogHeader><DialogTitle className="font-display">Replace or add to what's there?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This field already has text. What should happen with the dictation?</p>
          <pre className="max-h-48 overflow-y-auto whitespace-pre-wrap border border-slate-200 bg-slate-50 p-3 font-mono text-xs text-slate-800"
               data-testid={`${testid}-preview`}>{result}</pre>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => applyResult("append")} data-testid={`${testid}-append`}
                    className="h-11 flex-1 bg-[#134e4a] hover:bg-[#0f3f3c]">
              <CornerDownRight size={15} className="mr-2" /> Add to the end
            </Button>
            <Button onClick={() => applyResult("replace")} variant="outline" data-testid={`${testid}-replace`}
                    className="h-11 flex-1 border-slate-300">
              <Replace size={15} className="mr-2" /> Replace everything
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
